import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  InputAdornment,
  MenuItem,
  Modal,
  Select,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import SearchIcon from "@mui/icons-material/Search";
import IndeterminateCheckBoxOutlinedIcon from "@mui/icons-material/IndeterminateCheckBoxOutlined";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useFormik } from "formik";
import * as yup from "yup";
import { RequiredAsterisk } from "../../UI/RequiredAsterisk/RequiredAsterisk";
import {
  useCreatePromotionInviteMutation,
  useGetPromotionsQuery,
} from "../../../redux-toolkit/features/promotions/promotionsApiSlice";
import { useGetAllCompaniesQuery } from "../../../redux-toolkit/features/company/companyApiSlice";
import { useAlert } from "../../../hooks/useAlert";
import {
  DESCRIPTION_MAX_LENGTH,
  descriptionQuillModules,
  getDescriptionLength,
} from "./quillDescriptionLimit";
import {
  combineEventDatePayload,
  formatUtcEventDate,
  promotionEventDateSchema,
  promotionTodayPickerValue,
  syncEventDateField,
} from "./promotionEventDateTime";
import { FieldCharCount } from "./FieldCharCount";
import {
  cappedTextSchema,
  locationUrlSchema,
  normalizeLocationUrl,
  PROMOTION_NAME_MAX_LENGTH,
  PROMOTION_VENUE_MAX_LENGTH,
  setClampedField,
  stripHtml,
} from "./promotionInviteFields";
import { CreateEventConflictPanel } from "./CreateEventConflictPanel";
import { PromotionTimePicker } from "./PromotionTimePicker";
import { CompanyFilterDropdown } from "./CompanyFilterDropdown";
import { CREATE_EVENT_VIEW } from "./promotionListModes";
import sortUp from "../../../assets/employee/sort_up.svg";
import sortDown from "../../../assets/employee/sort_down.svg";
import "./CreatePromotionInviteModal.css";
import "./AddEmployeesToEventModal.css";

const STEPS = {
  DETAILS: "details",
  EMPLOYEES: "employees",
  CONFLICTS: "conflicts",
  EMPTY: "empty",
};

const CONFLICT_TYPES = {
  ASSIGNMENT: "ASSIGNMENT",
  SCHEDULE: "SCHEDULE",
};

const detailsModalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 560,
  maxWidth: "calc(100vw - 32px)",
  maxHeight: "90vh",
  overflowY: "auto",
  bgcolor: "background.paper",
  borderRadius: "12px",
  boxShadow: 24,
  p: "24px 28px 20px",
  outline: "none",
};

const employeesModalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 560,
  maxWidth: "calc(100vw - 32px)",
  maxHeight: "90vh",
  display: "flex",
  flexDirection: "column",
  bgcolor: "background.paper",
  borderRadius: "12px",
  boxShadow: 24,
  outline: "none",
  overflow: "hidden",
  p: 0,
};

const quillFormats = ["bold", "italic", "underline", "align", "list", "bullet"];

const getInitialValues = (defaultBookId = "") => ({
  bookId: defaultBookId || "",
  promotionName: "",
  description: "",
  eventDate: null,
  eventTime: null,
  venue: "",
  locationUrl: "",
});

const validationSchema = yup.object({
  bookId: yup.string().trim().required("Book is required"),
  promotionName: cappedTextSchema(
    PROMOTION_NAME_MAX_LENGTH,
    `Promotion name cannot exceed ${PROMOTION_NAME_MAX_LENGTH} characters`,
    { required: "Promotion name is required" }
  ),
  description: yup
    .string()
    .test(
      "description-required",
      "Description is required",
      (value) => Boolean(stripHtml(value))
    )
    .test(
      "description-max",
      `Description cannot exceed ${DESCRIPTION_MAX_LENGTH} characters`,
      (value) => getDescriptionLength(value) <= DESCRIPTION_MAX_LENGTH
    ),
  eventDate: promotionEventDateSchema(),
  eventTime: yup.mixed().nullable(),
  venue: cappedTextSchema(
    PROMOTION_VENUE_MAX_LENGTH,
    `Venue cannot exceed ${PROMOTION_VENUE_MAX_LENGTH} characters`
  ),
  locationUrl: locationUrlSchema(),
});

const compareByName = (a, b) =>
  String(a || "").localeCompare(String(b || ""), undefined, {
    sensitivity: "base",
  });

const sortEmployees = (rows, direction = "asc") =>
  [...rows].sort((a, b) => {
    const result =
      compareByName(a.employee?.name, b.employee?.name) ||
      compareByName(a.company?.name, b.company?.name);
    return direction === "desc" ? -result : result;
  });

const textFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    backgroundColor: "#fff",
    "& fieldset": { borderColor: "var(--grey300, #d0d5dd)" },
    "&:hover fieldset": { borderColor: "var(--grey400, #98a2b3)" },
    "&.Mui-focused fieldset": { borderColor: "var(--pblue)" },
  },
  "& .MuiInputBase-input": {
    fontSize: "14px",
    padding: "12px 14px",
  },
};

export const CreateEventWizardModal = ({
  open,
  onClose,
  books = [],
  defaultBookId = "",
  initialPromotionIds = [],
  onSuccess,
}) => {
  const { displayAlert } = useAlert();
  const [createInvite, { isLoading }] = useCreatePromotionInviteMutation();
  const [step, setStep] = useState(STEPS.DETAILS);
  const [conflicts, setConflicts] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [companyIds, setCompanyIds] = useState([]);
  const [sortDir, setSortDir] = useState("asc");
  const [detailsSnapshot, setDetailsSnapshot] = useState(null);

  const formik = useFormik({
    initialValues: getInitialValues(defaultBookId),
    validationSchema,
    enableReinitialize: false,
    onSubmit: async () => {},
  });

  const selectedBookId = formik.values.bookId;
  const selectedBookName =
    books.find((b) => String(b._id) === String(selectedBookId))?.name || "";

  const { data: companiesData } = useGetAllCompaniesQuery("", {
    skip: !open || step !== STEPS.EMPLOYEES,
  });
  const companies = useMemo(() => {
    const companiesRaw =
      companiesData?.data?.companies || companiesData?.data || [];
    const list = Array.isArray(companiesRaw) ? [...companiesRaw] : [];
    return list.sort((a, b) => compareByName(a.name, b.name));
  }, [companiesData]);

  const {
    data: eligibleData,
    isLoading: eligibleLoading,
    isFetching: eligibleFetching,
  } = useGetPromotionsQuery(
    {
      bookId: selectedBookId,
      view: CREATE_EVENT_VIEW,
      search: debouncedSearch || undefined,
      companyIds: companyIds.length ? companyIds : undefined,
      page: 1,
      limit: 200,
    },
    {
      skip: !open || step !== STEPS.EMPLOYEES || !selectedBookId,
      refetchOnMountOrArgChange: true,
    }
  );

  const employees = useMemo(() => {
    const allEmployees = eligibleData?.data?.promotions?.list || [];
    return sortEmployees(allEmployees, sortDir);
  }, [eligibleData, sortDir]);

  useEffect(() => {
    if (!open) return;
    setStep(STEPS.DETAILS);
    setConflicts([]);
    setSelectedIds(
      Array.isArray(initialPromotionIds)
        ? initialPromotionIds.filter(Boolean)
        : []
    );
    setSearch("");
    setDebouncedSearch("");
    setCompanyIds([]);
    setSortDir("asc");
    setDetailsSnapshot(null);
    formik.resetForm({ values: getInitialValues(defaultBookId) });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset only when modal opens
  }, [open, defaultBookId, initialPromotionIds]);

  useEffect(() => {
    if (!open || step !== STEPS.EMPLOYEES || !employees.length) return;
    if (!Array.isArray(initialPromotionIds) || !initialPromotionIds.length) {
      return;
    }
    const eligible = new Set(employees.map((row) => row._id));
    setSelectedIds((prev) => {
      const seeded = initialPromotionIds.filter((id) => eligible.has(id));
      if (!seeded.length) return prev;
      // Keep any manual picks already made on this step, union with seed.
      return [...new Set([...prev, ...seeded])];
    });
  }, [open, step, employees, initialPromotionIds]);

  useEffect(() => {
    if (!open || step !== STEPS.EMPLOYEES) return;
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search, open, step]);

  const descriptionLength = getDescriptionLength(formik.values.description);
  const promotionNameLength = String(formik.values.promotionName || "").length;
  const venueLength = String(formik.values.venue || "").length;

  const handleDescriptionChange = (content, _delta, source, editor) => {
    const length = Math.max(0, (editor?.getLength?.() ?? 1) - 1);
    if (length > DESCRIPTION_MAX_LENGTH) return;
    formik.setFieldValue("description", content);
  };

  const handleClose = () => {
    if (isLoading) return;
    onClose?.();
  };

  const hasSeededEmployees =
    Array.isArray(initialPromotionIds) && initialPromotionIds.length > 0;

  const markDetailsTouched = () => {
    formik.setTouched({
      bookId: true,
      promotionName: true,
      description: true,
      eventDate: true,
      venue: true,
      locationUrl: true,
    });
  };

  /** Top-bar Create Event: details → pick employees. Snackbar: details → create. */
  const handleDetailsContinue = async () => {
    const errors = await formik.validateForm();
    if (Object.keys(errors).length) {
      markDetailsTouched();
      return;
    }
    const values = { ...formik.values };
    setDetailsSnapshot(values);
    setConflicts([]);
    if (hasSeededEmployees) {
      await submitInvite({ removeConflicting: false, values });
      return;
    }
    setStep(STEPS.EMPLOYEES);
  };

  const submitInvite = async ({ removeConflicting, values: valuesOverride }) => {
    const values = valuesOverride || detailsSnapshot || formik.values;
    const bookId = values.bookId;
    if (!bookId) {
      displayAlert({
        open: true,
        message: "Book is required",
        type: "error",
        timeout: "2500",
      });
      return;
    }

    const promotionIds = removeConflicting
      ? selectedIds.filter((id) => !conflicts.some((c) => c.promotionId === id))
      : selectedIds;

    if (!promotionIds.length) {
      setConflicts([]);
      setSelectedIds([]);
      setStep(STEPS.EMPTY);
      return;
    }

    const { eventDate: eventDateIso, hasEventTime } = combineEventDatePayload(
      values.eventDate,
      values.eventTime
    );
    if (!eventDateIso) {
      displayAlert({
        open: true,
        message: "Valid date is required",
        type: "error",
        timeout: "2500",
      });
      return;
    }

    try {
      const result = await createInvite({
        bookId,
        promotionIds,
        name: String(values.promotionName || "").trim(),
        description: values.description,
        eventDate: eventDateIso,
        hasEventTime,
        venue: String(values.venue || "").trim(),
        locationUrl: normalizeLocationUrl(values.locationUrl),
        removeConflicting,
      }).unwrap();

      displayAlert({
        open: true,
        message: "Promotion invite created successfully",
        type: "success",
        timeout: "2500",
      });
      onSuccess?.(result?.data?.invite);
      handleClose();
    } catch (err) {
      if (err?.status === 409 && err?.data?.data?.conflicts?.length) {
        setConflicts(err.data.data.conflicts);
        setStep(STEPS.CONFLICTS);
        return;
      }
      if (
        err?.status === 400 &&
        /at least one employee/i.test(err?.data?.message || err?.message || "")
      ) {
        setConflicts([]);
        setSelectedIds([]);
        setStep(STEPS.EMPTY);
        return;
      }
      displayAlert({
        open: true,
        message:
          err?.data?.message ||
          err?.message ||
          "Unable to create promotion invite",
        type: "error",
        timeout: "3000",
      });
    }
  };

  const handleRemoveConflict = (promotionId) => {
    setConflicts((prev) => prev.filter((c) => c.promotionId !== promotionId));
    setSelectedIds((prev) => prev.filter((id) => id !== promotionId));
  };

  const handleRemoveAndContinue = async () => {
    await submitInvite({ removeConflicting: true });
  };

  const selectedCount = selectedIds.length;
  const allSelected =
    employees.length > 0 &&
    employees.every((row) => selectedIds.includes(row._id));
  const someSelected =
    employees.some((row) => selectedIds.includes(row._id)) && !allSelected;

  const toggleAll = (checked) => {
    setSelectedIds(checked ? employees.map((row) => row._id) : []);
  };

  const toggleOne = (id, checked) => {
    setSelectedIds((prev) =>
      checked ? [...prev, id] : prev.filter((x) => x !== id)
    );
  };

  const isEmptySelection = step === STEPS.EMPTY;
  const isScheduleConflict =
    conflicts.length > 0 && conflicts[0]?.type === CONFLICT_TYPES.SCHEDULE;
  const selectedConflictDate = formatUtcEventDate(
    conflicts[0]?.conflictingEvent?.eventDate || formik.values.eventDate
  );

  return (
    <Modal
      open={open}
      onClose={isLoading ? undefined : handleClose}
      disableEnforceFocus
      aria-labelledby="create-event-wizard-title"
    >
      <Box
        sx={
          step === STEPS.EMPLOYEES ? employeesModalStyle : detailsModalStyle
        }
        className={
          step === STEPS.EMPLOYEES
            ? "add-employees-modal"
            : "create-promo-invite-modal"
        }
      >
        {step === STEPS.DETAILS && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleDetailsContinue();
            }}
          >
            <div className="create-promo-invite-modal__head">
              <h2 id="create-event-wizard-title">Create Event</h2>
              <button
                type="button"
                className="create-promo-invite-modal__close"
                onClick={handleClose}
                disabled={isLoading}
                aria-label="Close"
              >
                <CloseIcon sx={{ fontSize: 18 }} />
              </button>
            </div>
            <div className="create-promo-invite-modal__separator" />

            <div className="create-promo-invite-modal__field">
              <label className="create-promo-invite-modal__label" htmlFor="bookId">
                Book Name <RequiredAsterisk />
              </label>
              <FormControl fullWidth>
                <Select
                  id="bookId"
                  name="bookId"
                  displayEmpty
                  value={formik.values.bookId}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.bookId && Boolean(formik.errors.bookId)}
                  sx={textFieldSx}
                >
                  <MenuItem value="" disabled>
                    Select book
                  </MenuItem>
                  {books.map((book) => (
                    <MenuItem key={book._id} value={book._id}>
                      {book.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {formik.touched.bookId && formik.errors.bookId ? (
                <p className="create-promo-invite-modal__error">
                  {formik.errors.bookId}
                </p>
              ) : null}
            </div>

            <div className="create-promo-invite-modal__field">
              <label
                className="create-promo-invite-modal__label"
                htmlFor="promotionName"
              >
                Promotion Name <RequiredAsterisk />
              </label>
              <TextField
                id="promotionName"
                name="promotionName"
                fullWidth
                placeholder="Enter Promotion Name"
                value={formik.values.promotionName}
                onChange={setClampedField(
                  formik,
                  "promotionName",
                  PROMOTION_NAME_MAX_LENGTH
                )}
                onBlur={formik.handleBlur}
                inputProps={{ maxLength: PROMOTION_NAME_MAX_LENGTH }}
                error={
                  formik.touched.promotionName &&
                  Boolean(formik.errors.promotionName)
                }
                sx={textFieldSx}
              />
              <FieldCharCount
                length={promotionNameLength}
                max={PROMOTION_NAME_MAX_LENGTH}
              />
              {formik.touched.promotionName && formik.errors.promotionName && (
                <p className="create-promo-invite-modal__error">
                  {formik.errors.promotionName}
                </p>
              )}
            </div>

            <div className="create-promo-invite-modal__field">
              <label className="create-promo-invite-modal__label">
                Description <RequiredAsterisk />
              </label>
              <div
                className={`create-promo-invite-modal__quill ${
                  formik.touched.description && formik.errors.description
                    ? "create-promo-invite-modal__quill--error"
                    : ""
                }`}
              >
                <ReactQuill
                  theme="snow"
                  value={formik.values.description}
                  onChange={handleDescriptionChange}
                  onBlur={() => formik.setFieldTouched("description", true)}
                  modules={descriptionQuillModules}
                  formats={quillFormats}
                  placeholder="Enter description"
                />
              </div>
              <FieldCharCount
                length={descriptionLength}
                max={DESCRIPTION_MAX_LENGTH}
              />
              {formik.touched.description && formik.errors.description && (
                <p className="create-promo-invite-modal__error">
                  {formik.errors.description}
                </p>
              )}
            </div>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <div className="create-promo-invite-modal__row">
                <div className="create-promo-invite-modal__field create-promo-invite-modal__field--half">
                  <label className="create-promo-invite-modal__label">
                    Select Date <RequiredAsterisk />
                  </label>
                  <DatePicker
                    value={formik.values.eventDate}
                    onChange={(value) => syncEventDateField(formik, value)}
                    disablePast
                    minDate={promotionTodayPickerValue()}
                    inputFormat="DD/MM/YYYY"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        placeholder="Select Date"
                        onBlur={() => formik.setFieldTouched("eventDate", true)}
                        error={
                          formik.touched.eventDate &&
                          Boolean(formik.errors.eventDate)
                        }
                        helperText={
                          formik.touched.eventDate && formik.errors.eventDate
                        }
                        sx={textFieldSx}
                      />
                    )}
                    components={{
                      OpenPickerIcon: CalendarTodayOutlinedIcon,
                    }}
                  />
                </div>
                <div className="create-promo-invite-modal__field create-promo-invite-modal__field--half">
                  <label className="create-promo-invite-modal__label">
                    Select Time
                  </label>
                  <PromotionTimePicker
                    applyDefaultOnOpen
                    value={formik.values.eventTime}
                    onChange={(value) =>
                      formik.setFieldValue("eventTime", value)
                    }
                    onBlur={() => formik.setFieldTouched("eventTime", true)}
                    error={
                      formik.touched.eventTime &&
                      Boolean(formik.errors.eventTime)
                    }
                    helperText={
                      formik.touched.eventTime && formik.errors.eventTime
                    }
                    textFieldSx={textFieldSx}
                  />
                </div>
              </div>
            </LocalizationProvider>

            <div className="create-promo-invite-modal__field">
              <label className="create-promo-invite-modal__label" htmlFor="venue">
                Venue Address
              </label>
              <TextField
                id="venue"
                name="venue"
                fullWidth
                placeholder="Enter Venue Address"
                value={formik.values.venue}
                onChange={setClampedField(
                  formik,
                  "venue",
                  PROMOTION_VENUE_MAX_LENGTH
                )}
                onBlur={formik.handleBlur}
                inputProps={{ maxLength: PROMOTION_VENUE_MAX_LENGTH }}
                error={formik.touched.venue && Boolean(formik.errors.venue)}
                sx={textFieldSx}
              />
              <FieldCharCount length={venueLength} max={PROMOTION_VENUE_MAX_LENGTH} />
              {formik.touched.venue && formik.errors.venue && (
                <p className="create-promo-invite-modal__error">
                  {formik.errors.venue}
                </p>
              )}
            </div>

            <div className="create-promo-invite-modal__field">
              <label
                className="create-promo-invite-modal__label"
                htmlFor="locationUrl"
              >
                Location URL
              </label>
              <TextField
                id="locationUrl"
                name="locationUrl"
                fullWidth
                placeholder="https://"
                value={formik.values.locationUrl}
                onChange={formik.handleChange}
                onBlur={(event) => {
                  const next = normalizeLocationUrl(event.target.value);
                  if (next !== formik.values.locationUrl) {
                    formik.setFieldValue("locationUrl", next, true);
                  }
                  formik.handleBlur(event);
                }}
                error={
                  formik.touched.locationUrl &&
                  Boolean(formik.errors.locationUrl)
                }
                helperText={
                  formik.touched.locationUrl && formik.errors.locationUrl
                }
                sx={textFieldSx}
              />
            </div>

            <div className="create-promo-invite-modal__actions">
              <Button
                type="button"
                className="create-promo-invite-modal__btn-cancel"
                variant="contained"
                disableElevation
                onClick={handleClose}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="create-promo-invite-modal__btn-primary"
                variant="contained"
                disableElevation
                disabled={isLoading}
              >
                {hasSeededEmployees
                  ? isLoading
                    ? "Creating…"
                    : "Create Event"
                  : "Continue"}
              </Button>
            </div>
          </form>
        )}

        {step === STEPS.EMPLOYEES && (
          <>
            <div className="add-employees-modal__head">
              <div>
                <h2 id="create-event-wizard-title">Add employees</h2>
                <p className="add-employees-modal__subtitle">
                  Add qualified employees from{" "}
                  <span className="add-employees-modal__book">
                    {selectedBookName || "this"}
                  </span>{" "}
                  book
                </p>
              </div>
              <button
                type="button"
                className="add-employees-modal__close"
                onClick={handleClose}
                disabled={isLoading}
                aria-label="Close"
              >
                <CloseIcon sx={{ fontSize: 18 }} />
              </button>
            </div>
            <div className="add-employees-modal__separator" />

            <div className="add-employees-modal__search-row">
              <TextField
                fullWidth
                size="small"
                placeholder="Search employees by name or department..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: "#98a2b3" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    background: "#fff",
                  },
                }}
              />
              <CompanyFilterDropdown
                companies={companies}
                appliedIds={companyIds}
                onApply={setCompanyIds}
                disabled={isLoading}
              />
            </div>

            <div className="add-employees-modal__list-head">
              <div className="add-employees-modal__list-head-main">
                <Checkbox
                  size="small"
                  checked={allSelected}
                  indeterminate={someSelected}
                  indeterminateIcon={<IndeterminateCheckBoxOutlinedIcon />}
                  onChange={(e) => toggleAll(e.target.checked)}
                  disabled={!employees.length || isLoading}
                  sx={{
                    color: "var(--pblue)",
                    "&.Mui-checked": { color: "var(--pblue)" },
                    "&.MuiCheckbox-indeterminate": { color: "var(--pblue)" },
                  }}
                />
                <span className="add-employees-modal__list-title">
                  Employees ({employees.length})
                </span>
              </div>
              <button
                type="button"
                className="add-employees-modal__sort-btn"
                onClick={() =>
                  setSortDir((prev) => (prev === "asc" ? "desc" : "asc"))
                }
                disabled={!employees.length || isLoading}
                aria-label={
                  sortDir === "asc"
                    ? "Sort employees Z to A"
                    : "Sort employees A to Z"
                }
                title={sortDir === "asc" ? "Sort Z–A" : "Sort A–Z"}
              >
                <img
                  src={sortUp}
                  alt=""
                  className={
                    sortDir === "asc"
                      ? "add-employees-modal__sort-icon--active"
                      : undefined
                  }
                />
                <img
                  src={sortDown}
                  alt=""
                  className={
                    sortDir === "desc"
                      ? "add-employees-modal__sort-icon--active"
                      : undefined
                  }
                />
              </button>
            </div>

            <div className="add-employees-modal__list">
              {eligibleLoading || eligibleFetching ? (
                <p className="add-employees-modal__empty">Loading employees…</p>
              ) : employees.length === 0 ? (
                <p className="add-employees-modal__empty">
                  No contact-pending employees for this book
                </p>
              ) : (
                employees.map((row) => {
                  const checked = selectedIds.includes(row._id);
                  return (
                    <label
                      key={row._id}
                      className={`add-employees-modal__row ${
                        checked ? "add-employees-modal__row--selected" : ""
                      }`}
                    >
                      <Checkbox
                        size="small"
                        checked={checked}
                        onChange={(e) => toggleOne(row._id, e.target.checked)}
                        disabled={isLoading}
                        sx={{
                          color: "var(--grey400)",
                          "&.Mui-checked": { color: "var(--pblue)" },
                        }}
                      />
                      <div className="add-employees-modal__row-info">
                        <span className="add-employees-modal__name">
                          {row.employee?.name || "—"}
                        </span>
                        <span className="add-employees-modal__dept">
                          {row.company?.name || "—"}
                        </span>
                      </div>
                    </label>
                  );
                })
              )}
            </div>

            <div className="add-employees-modal__footer">
              <span className="add-employees-modal__count">
                <span className="add-employees-modal__count-dot" />
                {selectedCount} employee{selectedCount === 1 ? "" : "s"}{" "}
                selected
              </span>
              <div className="add-employees-modal__actions">
                <Button
                  type="button"
                  variant="outlined"
                  className="add-employees-modal__btn-cancel"
                  onClick={() => setStep(STEPS.DETAILS)}
                  disabled={isLoading}
                >
                  Back
                </Button>
                <Button
                  type="button"
                  variant="contained"
                  className="add-employees-modal__btn-confirm"
                  disableElevation
                  onClick={() => submitInvite({ removeConflicting: false })}
                  disabled={isLoading || !selectedIds.length}
                >
                  {isLoading ? "Creating…" : "Create Event"}
                </Button>
              </div>
            </div>
          </>
        )}

        {(step === STEPS.EMPTY || step === STEPS.CONFLICTS) && (
          <CreateEventConflictPanel
            empty={isEmptySelection}
            conflicts={conflicts}
            isScheduleConflict={isScheduleConflict}
            selectedConflictDate={selectedConflictDate}
            isLoading={isLoading}
            onClose={handleClose}
            onBackToSelection={() =>
              hasSeededEmployees ? handleClose() : setStep(STEPS.EMPLOYEES)
            }
            onRemoveConflict={handleRemoveConflict}
            onRemoveAndContinue={handleRemoveAndContinue}
          />
        )}
      </Box>
    </Modal>
  );
};
